import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import NavigateNextIcon from "@mui/icons-material/NavigateNext"
import {
    Breakpoint,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    SvgIconProps,
    Typography,
} from "@mui/material"
import { ReactElement, ReactNode } from "react"

import { IUseScreenSize, useScreenSize } from "../../../hooks/useScreenSize"
import { GRID_SPACING } from "../../../themes/defaultThemeParams"
import AppButton from "../../buttons/appButton"
import CopyTypography from "../../dataDisplay/typography/copyTypography"
import AppDialog from "../../dialog/appDialog"
import { AppCardHeader } from "../../surfaces/appCard"

interface ConfirmationProps {
    title: string
    subtitle: string
    isOpen: boolean
    children: ReactNode
    successIdName: string
    successId?: string
    nextButtonTitle: string
    color: "success" | "primary"
    onClickNext: () => void
    icon: (props: SvgIconProps) => ReactElement
}

/**
 * A simple fullscreen dialog shown to the user after successfully submitting
 * a form. This is a generic wrapper for this type of input, to ensure
 * consistency in the UI. There are some fixed inputs / styling and a children
 * prop to allow for customisation.
 */
const SuccessConfirmationTemplate = ({
    title,
    subtitle,
    isOpen,
    children,
    successId,
    successIdName,
    nextButtonTitle,
    color,
    onClickNext,
    icon,
}: ConfirmationProps): ReactElement => {
    const screenSize = useScreenSize()

    const { isMob, isTabletOrMob } = screenSize

    const getUpperBoundBreakPointFromScreenSize = (
        size: IUseScreenSize
    ): Breakpoint => {
        if (size.isMob) {
            return "sm"
        }

        if (size.isTablet) {
            return "md"
        }

        if (!size.isTabletOrMob && !size.isLg) {
            return "lg"
        }

        if (size.isLg || size.isXl) {
            return "xl"
        }

        return "md"
    }

    const upperBoundBreakpoint: Breakpoint =
        getUpperBoundBreakPointFromScreenSize(screenSize)

    const iconSizeMapping: Record<Breakpoint, number> = {
        xs: 80,
        sm: 80,
        md: 100,
        lg: 115,
        xl: 150,
    }

    const marginMapping: Record<Breakpoint, string> = {
        xs: "0px",
        sm: "0px",
        md: "15%",
        lg: "25%",
        xl: "30%",
    }

    const iconSize = iconSizeMapping[upperBoundBreakpoint]
    const successIdSize = isMob ? undefined : 25
    const stackMargin = marginMapping[upperBoundBreakpoint]
    const titleTypographyProps = isMob
        ? undefined
        : {
              fontSize: 38,
          }
    const subheaderTypographyProps = isMob
        ? undefined
        : {
              mt: 2,
              fontSize: 22,
          }

    return (
        <AppDialog
            sx={{
                justifyContent: "center",
            }}
            isOpen={isOpen}
            isFullscreen
        >
            <Stack
                alignItems="center"
                textAlign="center"
                marginY={GRID_SPACING}
                marginX={stackMargin}
            >
                {icon({ sx: { fontSize: iconSize } })}
                <DialogTitle mt={isTabletOrMob ? 3 : 4}>
                    <AppCardHeader
                        title={title}
                        subheader={subtitle}
                        titleTypographyProps={{
                            color: "text.primary",
                            ...titleTypographyProps,
                        }}
                        subheaderTypographyProps={subheaderTypographyProps}
                    />
                </DialogTitle>
                <DialogContent sx={{ width: "100%" }}>{children}</DialogContent>
                <DialogContent
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                    }}
                >
                    <Typography
                        color="text.primary"
                        fontWeight="450"
                        fontSize={successIdSize}
                    >
                        {successIdName}
                    </Typography>
                    <CopyTypography
                        typographyProps={{
                            fontSize: successIdSize,
                            marginLeft: 1,
                        }}
                        text={`${successId}`}
                        customCopyText={successId}
                        customTooltip={`${successId} - Click to copy to clipboard`}
                        endIcon={<ContentCopyIcon sx={{ ml: 2 }} />}
                    />
                </DialogContent>
                <DialogActions>
                    <AppButton
                        variant="contained"
                        color={color}
                        endIcon={<NavigateNextIcon />}
                        disabled={!successId}
                        onClick={onClickNext}
                    >
                        {nextButtonTitle}
                    </AppButton>
                </DialogActions>
            </Stack>
        </AppDialog>
    )
}

SuccessConfirmationTemplate.defaultProps = {
    successId: undefined,
}

export default SuccessConfirmationTemplate
